import { FormEvent, forwardRef, RefObject, useEffect, useRef } from 'react';
import { useMachine } from '@xstate/react';
import { StateFrom } from 'xstate';
import { Box } from '../Box/Box';
import { Button } from '../Button/Button';
import { Error } from '../Error/Error';
import { Heading } from '../Heading/Heading';
import { InfoModal, useInfoModalState } from '../InfoModal/InfoModal';
import { Paragraph } from '../Paragraph/Paragraph';
import { Stack } from '../Stack/Stack';
import { FEEDBACK_CATEGORY } from './constants';
import {
  Context as MachineContext,
  FeedbackProps,
  RequestFn,
  Settings,
} from './types';
import { Stars } from './inner/Stars';
import { TextArea } from './inner/Textarea';
import { ThankYouMessage } from './inner/ThankYouMessage';
import { feedbackMachine } from './statemachine';
import { useFeedbackRequest } from './useFeedbackRequest';
import { useRequestService } from './useRequestService';
import { shouldNeverHappen } from '../utils/shouldBeverHappen';

export type {
  FeedbackProps,
  MetricEvent,
  PropsWithRequest,
  PropsWithSettings,
  Settings,
  RequestFn,
  CommonProps,
  DeprecatedSettings,
  RequestFnParams,
  ResponseBody,
  RequestBody,
} from './types';

/*
 * Временная мера для того, чтобы использовать этот хук внутри компонента с большим количеством пропсов,
 * пока мы не уберем обратную совместимость.
 */
const requestFnHook = useFeedbackRequest as (params: Settings) => RequestFn;
export { FEEDBACK_CATEGORY, requestFnHook as useFeedbackRequest };

let scrollIntoViewOptions: ScrollIntoViewOptions = {
  // Скроллим так как пользователь настроил свой браузер.
  behavior: 'auto',
  block: 'center',
};

function FeedbackHeading({ title, titleLevel = 2 }: FeedbackProps) {
  return (
    <Box>
      <Heading level={titleLevel}>
        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Здесь важно срабатывание в том числе на пустую строку, например */}
        {title || 'Оцените удобство сервиса'}
      </Heading>
    </Box>
  );
}

let RatingError = forwardRef<
  HTMLElement,
  { ratingError: MachineContext['rating_error'] }
>(function CommentError({ ratingError }, ref) {
  switch (ratingError) {
    case 'RATING_NOT_SELECTED': {
      return (
        <Error
          ref={ref}
          tabIndex={-1}
          styles={{
            '&:focus, &:focus-visible': {
              outline: 0,
            },
          }}
        >
          Выберите, какую оценку вы хотите поставить
        </Error>
      );
    }
    case null: {
      return null;
    }
    default: {
      shouldNeverHappen(ratingError);
    }
  }
  return null;
});

function focusByRef(ref: RefObject<HTMLElement>): void {
  ref.current?.focus();
  ref.current?.scrollIntoView(scrollIntoViewOptions);
}

function getCommentError(formState: StateFrom<typeof feedbackMachine>) {
  if (!formState.matches({ errors: 'shown' })) return undefined;
  switch (formState.context.comment_error) {
    case 'COMMENT_TOO_LONG': {
      return 'Введите текст не длиннее 400 символов';
    }
    case null: {
      return undefined;
    }
    default: {
      shouldNeverHappen(formState.context.comment_error);
      return undefined;
    }
  }
}

export function Feedback(props: FeedbackProps) {
  let commentRef = useRef<HTMLTextAreaElement>(null);
  let ratingErrorRef = useRef<HTMLElement>(null);
  let thankYouRef = useRef<HTMLElement>(null);

  let request = useRequestService(useFeedbackRequest(props));

  let [state, send] = useMachine(feedbackMachine, {
    services: {
      request,
    },
  });

  let { onSubmit, eno, serviceId, xCallerId, category } = props;

  // Возвращает массив строк с текущим состоянием машины
  let effectDeps = state.toStrings();
  let modal = useInfoModalState();

  useEffect(function focusOnError() {
    if (!state.matches({ errors: 'shown' })) return;
    if (state.context.rating_error !== null) {
      focusByRef(ratingErrorRef);
    } else if (state.context.comment_error !== null) {
      focusByRef(commentRef);
    }
    /*
      eslint-disable-next-line react-hooks/exhaustive-deps -- В зависимости нет смысла указывать
      весь state, потому что эффект будет срабатывать на любое изменение, а мы смотрим на изменение
      конкретных значений
    */
  }, effectDeps);

  useEffect(function focusThankYouMessage() {
    if (!state.matches('sent')) return;
    focusByRef(thankYouRef);
    /*
      eslint-disable-next-line react-hooks/exhaustive-deps -- В зависимости нет смысла указывать
      весь state, потому что эффект будет срабатывать на любое изменение, а мы смотрим на изменение
      конкретных значений
    */
  }, effectDeps);

  useEffect(function showModal() {
    if (state.matches({ form: 'request_error' })) {
      modal.show();
    }
    /*
      eslint-disable-next-line react-hooks/exhaustive-deps -- В зависимости нет смысла указывать
      весь state, потому что эффект будет срабатывать на любое изменение, а мы смотрим на изменение
      конкретных значений
    */
  }, effectDeps);

  useEffect(function sendMetricEvent() {
    if (!state.matches({ form: 'sent' })) return;
    onSubmit?.({
      'Обратная связь': {
        'Форма оценки': {
          'Отправка оценки': {
            'Клик по кнопке "Отправить оценку"': {
              [Number(state.context.rating)]: state.context.comment,
            },
          },
        },
      },
    });
  });

  useEffect(
    function updatePropsInMachineContext() {
      send({
        type: 'update_props',
        payload: {
          eno,
          serviceId,
          xCallerId,
          category,
        },
      });
    },
    [send, eno, serviceId, xCallerId, category]
  );

  function closeModal() {
    send({ type: 'modal_close' });
    modal.hide();
  }

  if (state.matches({ form: 'sent' }))
    return (
      <Stack space="sp-24">
        <FeedbackHeading {...props} />
        <Box>
          <ThankYouMessage
            tabIndex={-1}
            ref={thankYouRef}
            sent={state.context.comment.length > 0 ? 'all' : 'rating'}
          />
        </Box>
      </Stack>
    );

  return (
    <>
      <Stack
        as="form"
        space="sp-24"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          send({ type: 'submit' });
        }}
      >
        <FeedbackHeading {...props} />
        <Stack space="sp-8">
          <RatingError
            ratingError={state.context.rating_error}
            ref={ratingErrorRef}
          />
          <Box alignSelf={['stretch', 'flex-start']}>
            <Stars
              selectedRating={state.context.rating}
              onRatingChange={(rating) => {
                send({ type: 'rating_change', payload: rating });
              }}
            />
          </Box>
        </Stack>
        <TextArea
          ref={commentRef}
          error={getCommentError(state)}
          symbolLimit={400}
          onChange={(event) => {
            send({ type: 'comment_change', payload: event.target.value });
          }}
        />
        <Button
          kind="primary"
          type="submit"
          loadingMessage="Отправляем оценку"
          styles={{ alignSelf: [null, 'flex-start'] }}
          loading={state.hasTag('loading_indication')}
        >
          Оценить
        </Button>
      </Stack>
      <InfoModal
        {...modal}
        hide={() => closeModal()}
        kind="error"
        heading="Не удалось отправить оценку"
      >
        <Stack space="sp-32" alignItems={[null, 'flex-start']}>
          <Paragraph>
            Произошла техническая ошибка при отправке оценки. Попробуйте еще раз
            или повторите попытку позже.
          </Paragraph>
          <Box display="flex" flexDirection={['column', 'row']}>
            <Button
              mr={[null, 'sp-16']}
              mb={['sp-8', 'sp-0']}
              kind="primary"
              onClick={() => {
                closeModal();
                send({ type: 'submit' });
              }}
            >
              Отправить еще раз
            </Button>
            <Button kind="secondary" onClick={() => closeModal()}>
              Закрыть
            </Button>
          </Box>
        </Stack>
      </InfoModal>
    </>
  );
}

Feedback.defaultProps = {
  category: FEEDBACK_CATEGORY.CONVENIENCE_OF_APPLYING,
};
